import { CompactText } from 'ui/CompactText';
// renderField.ts

import { useCategories } from 'hooks/useCategories';
import { AutocompleteField } from 'components/formsV2/fields/autocomplete';
import { PriceRangeInput } from 'components/formsV2/fields/PriceAmountField';
import { MultiSelectCheckboxField } from 'components/formsV2/fields/select/MultiSelectCheckboxField';

import { StudyTimePicker } from 'components/StudyTimePicker/StudyTimePicker';
import type { StudyTime } from 'utils/studyTime';

import { isGoalAllowed } from 'utils/goalFit';
import { DEFAULT_SUBJECT } from './taskForm.store';
import styles from './TaskWizardModal.module.scss';

const GRADE_OPTIONS = Array.from({ length: 11 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1} класс`,
}));

const WISHES_MAX = 2000;
const PRICE_MIN = 500;
const PRICE_MAX = 20000;

type ProfileContext = {
  registrant_type?: 'self' | 'parent';
};


export const renderStep = (
  stepKey: string,
  values: Record<string, unknown>,
  setFieldValue: (field: string, value: unknown) => void,
  errors: Record<string, string | undefined>,
  touched: Record<string, boolean | undefined>,
  _profile?: ProfileContext,
) => {
  const { data: categories } = useCategories();

  const goalOptions = (() => {
    if (!categories) return [];
    const subjectSlug = String(values.subject || DEFAULT_SUBJECT);
    const category = categories.find((cat) => cat.name === subjectSlug);
    return (
      category?.subcategories
        ?.filter((sub) =>
          isGoalAllowed(sub.name, values.studentType as string | undefined, values.grades as string[] | undefined),
        )
        .map((sub) => ({
        value: sub.name,
        label: (sub as { title?: string }).title || sub.name,
      })) || []
    );
  })();

  const loadGoals = async (inputValue: string) => {
    return goalOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  const Error = ({ name }: { name: string }) =>
    touched[name] && errors[name] ? (
      <div className='form-error-message'>{errors[name]}</div>
    ) : null;

  switch (stepKey) {
    case 'learnerType':
      return (
        <>
          <div className={styles.radioGroup}>
            {[
              { value: 'school', label: 'Школьник' },
              { value: 'graduated', label: 'Студент' },
            ].map((option) => (
              <label key={option.value} className={styles.radioItem}>
                <input
                  type='radio'
                  value={option.value}
                  checked={values.studentType === option.value}
                  onChange={(e) => {
                    const next = e.target.value;
                    setFieldValue('studentType', next);
                    if (next !== 'school') {
                      setFieldValue('grades', []);
                    }
                  }}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
          <Error name='studentType' />
        </>
      );

    case 'grades':
      return (
        <>
          <MultiSelectCheckboxField
            name='grades'
            label=''
            options={GRADE_OPTIONS}
            placeholder='Выберите класс'
          />
          {/* Ошибку показывает само поле выбора; второй текст дублировал её. */}
        </>
      );

    case 'subjectGoal':
      return (
        <>
          <AutocompleteField
            name='goal'
            label=''
            options={goalOptions}
            loadOptions={loadGoals}
            onChange={(value, option) => {
              setFieldValue('goal', value);
              setFieldValue('goalLabel', option?.label || '');
              if (!values.subject) {
                setFieldValue('subject', DEFAULT_SUBJECT);
                setFieldValue('subjectLabel', 'Математика');
              }
            }}
          />
          <Error name='goal' />
        </>
      );

    case 'teacherGender':
      return (
        <>
          <div className={styles.radioGroup}>
            {[
              { value: 'male', label: 'Мужчина' },
              { value: 'female', label: 'Женщина' },
              { value: 'any', label: 'Любой' },
            ].map((g) => (
              <label key={g.value} className={styles.radioItem}>
                <input
                  type='radio'
                  value={g.value}
                  checked={values.teacherGender === g.value}
                  onChange={(e) => setFieldValue('teacherGender', e.target.value)}
                />
                <span>{g.label}</span>
              </label>
            ))}
          </div>
          <Error name='teacherGender' />
        </>
      );

    case 'duration': {
      const durationOptions = [
        { value: 'express', label: 'Экспресс-занятие', desc: '1-2 занятия' },
        { value: 'short', label: 'На короткий срок', desc: 'До 1 месяца' },
        { value: 'long', label: 'На длительный срок', desc: 'Более месяца' },
        { value: 'later', label: 'Решу позже', desc: 'Пока не определился' },
      ];

      return (
        <>
          <div className={styles.radioGroup}>
            {durationOptions.map((opt) => (
              <div key={opt.value}>
                <label className={styles.radioItem}>
                  <input
                    type='radio'
                    value={opt.value}
                    checked={values.duration === opt.value}
                    onChange={(e) => setFieldValue('duration', e.target.value)}
                  />
                  <span>{opt.label}</span>
                </label>
                <div
                  className={`${styles.durationDescription} ${
                    values.duration === opt.value ? styles.durationActive : ''
                  }`}
                >
                  {opt.desc}
                </div>
              </div>
            ))}
          </div>
          <Error name='duration' />
        </>
      );
    }

    case 'locations': {
      const locations = ['Онлайн', 'У репетитора', 'У меня', 'Неважно'];
      const selected = (values.locations as string[]) || [];

      return (
        <>
          {locations.map((loc) => {
            const isAny = loc === 'Неважно';
            // «Неважно» сбрасывает остальные, выбор варианта снимает «Неважно» (без блокировки).
            const disabled = false;

            return (
              <label
                key={loc}
                className={`${styles.checkboxLabel} ${disabled ? styles.checkboxDisabled : ''}`}
              >
                <input
                  type='checkbox'
                  checked={selected.includes(loc)}
                  disabled={disabled}
                  onChange={(e) => {
                    if (isAny) {
                      setFieldValue('locations', e.target.checked ? ['Неважно'] : []);
                      return;
                    }

                    const withoutAny = selected.filter((l) => l !== 'Неважно');
                    const updated = e.target.checked
                      ? [...withoutAny, loc]
                      : withoutAny.filter((l) => l !== loc);
                    setFieldValue('locations', updated);
                  }}
                />
                <span className={styles.customCheckbox} />
                <span>{loc}</span>
              </label>
            );
          })}
          <Error name='locations' />
        </>
      );
    }

    case 'schedule':
      return (
        <>
          <CompactText className={styles.hint} tone={'info'}>Выберите дни и время для занятий.</CompactText>
          <StudyTimePicker
            value={values.studyTime as StudyTime | null}
            onChange={(next) => setFieldValue('studyTime', next)}
            error={touched.studyTime && errors.studyTime ? String(errors.studyTime) : ''}
          />
        </>
      );

    case 'comment': {
      const comment = String(values.comment || '');
      return (
        <>
          <div className={styles.textareaWrap}>
            <textarea
              className={styles.textarea}
              placeholder='Пожелания к занятию (необязательно)'
              value={comment}
              maxLength={WISHES_MAX}
              rows={5}
              onChange={(e) => setFieldValue('comment', e.target.value)}
            />
          </div>
          <p className={styles.charCounter}>
            {comment.length} / {WISHES_MAX}
          </p>
          <Error name='comment' />
        </>
      );
    }

    case 'price': {
      return (
        <>
          <CompactText className={styles.hint} tone={'info'}>
            Минимум 500 рублей в час. Можно указать только от или только до.
          </CompactText>
          <PriceRangeInput
            className={styles.priceRow}
            shape='brick'
            independent
            fromValue={String(values.priceFrom ?? '')}
            toValue={String(values.priceTo ?? '')}
            onFromChange={(v) => setFieldValue('priceFrom', v ? Number(v) : null)}
            onToChange={(v) => setFieldValue('priceTo', v ? Number(v) : null)}
            min={PRICE_MIN}
            max={PRICE_MAX}
            fromPlaceholder='500'
            toPlaceholder='не обязательно'
          />
          {(touched.priceFrom || touched.priceTo) && (errors.priceFrom || errors.priceTo) && (
            <div className='form-error-message'>{errors.priceFrom || errors.priceTo}</div>
          )}
        </>
      );
    }

    case 'trial':
      return (
        <>
          <div className={styles.radioGroup}>
            <label className={styles.radioItem}>
              <input
                type='radio'
                value='yes'
                checked={values.trial === 'yes'}
                onChange={(e) => setFieldValue('trial', e.target.value)}
              />
              <span>Да, хочу</span>
            </label>
            <label className={styles.radioItem}>
              <input
                type='radio'
                value='no'
                checked={values.trial === 'no'}
                onChange={(e) => setFieldValue('trial', e.target.value)}
              />
              <span>Нет, не хочу</span>
            </label>
          </div>
          <Error name='trial' />
        </>
      );

    default:
      return null;
  }
};
